import { useState } from "react";
import { toast } from "sonner";
import { PageHero, Container } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import { Send } from "lucide-react";

export default function NewMemberForms() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", date_of_birth: "", baptized: false,
    previous_church: "", interests: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/new-member", form);
      setDone(true);
      toast.success("Welcome! Your form was received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PageHero
        overline="Welcome Home"
        title={<>We're <em className="text-gold-light italic font-light">honored</em> to have you.</>}
        kicker="Our membership team will reach out personally within a week."
      />
    );
  }

  return (
    <div data-testid="new-member-forms-page">
      <PageHero
        overline="New Member Form"
        title={<>Take the <em className="text-gold-light italic font-light">first step</em>.</>}
        kicker="Tell us a little about yourself so we can welcome you well."
      />

      <Container className="pb-32">
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-x-10 gap-y-7 max-w-4xl" data-testid="new-member-form">
          <Field label="First Name" required>
            <input data-testid="nm-first" required value={form.first_name} onChange={u("first_name")} className={inputCls} />
          </Field>
          <Field label="Last Name" required>
            <input data-testid="nm-last" required value={form.last_name} onChange={u("last_name")} className={inputCls} />
          </Field>
          <Field label="Email" required>
            <input data-testid="nm-email" type="email" required value={form.email} onChange={u("email")} className={inputCls} />
          </Field>
          <Field label="Phone" required>
            <input data-testid="nm-phone" type="tel" required value={form.phone} onChange={u("phone")} className={inputCls} />
          </Field>
          <Field label="Address" className="md:col-span-2">
            <input data-testid="nm-address" value={form.address} onChange={u("address")} className={inputCls} />
          </Field>
          <Field label="Date of Birth">
            <input data-testid="nm-dob" type="date" value={form.date_of_birth} onChange={u("date_of_birth")} className={inputCls} />
          </Field>
          <Field label="Previous Church (if any)">
            <input data-testid="nm-prev" value={form.previous_church} onChange={u("previous_church")} className={inputCls} />
          </Field>
          <Field label="Ministries you'd love to explore" className="md:col-span-2">
            <textarea data-testid="nm-interests" rows={4} value={form.interests} onChange={u("interests")} className={inputCls} placeholder="e.g. Music, Youth, Outreach…" />
          </Field>
          <label className="md:col-span-2 flex items-center gap-3 text-sm text-white/80">
            <input type="checkbox" checked={form.baptized} onChange={u("baptized")} className="h-4 w-4 accent-wine-500" data-testid="nm-baptized" />
            I have been baptized
          </label>
          <div className="md:col-span-2 pt-6 border-t border-white/10 flex justify-end">
            <button type="submit" disabled={submitting} className="btn-wine px-10 py-3.5 rounded-full text-sm uppercase tracking-widest font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="nm-submit">
              <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-0 border-b border-white/15 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors placeholder:text-white/30";
function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-semibold">{label}{required && <span className="text-gold ml-1">*</span>}</div>
      {children}
    </label>
  );
}
